import React, { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Medal, Search, Tags } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { useAuth } from "contexts/AuthContext";
import { useDialog } from "contexts/DialogContext";
import { useI18n } from "i18n";
import api from "lib/api";

/**
 * Lista de modalidades (Basquete, Vôlei…) — o que o formulario do evento
 * oferece no campo "Modalidade". A exclusao e logica: evento que ja usa a
 * modalidade continua exibindo o nome dela.
 */
export default function Sports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { confirm, alert } = useDialog();
  const { t, tf } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const canManage = !!user?.capabilities?.manage_events;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/sports/", {
        params: { search: search || undefined },
      });
      setItems(res.data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (!canManage) return undefined;
    const timer = setTimeout(fetchItems, 300);
    return () => clearTimeout(timer);
  }, [fetchItems, canManage]);

  // Menor privilegio: quem nao administra o acervo nao chega nesta tela.
  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const handleDelete = async (item) => {
    let failed = false;
    const confirmed = await confirm({
      title: t("sport.deleteTitle"),
      description: tf("sport.deleteBody", { name: item.name }),
      variant: "danger",
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel"),
      onConfirm: async () => {
        try {
          await api.delete(`/api/sports/${item.id}/`);
        } catch (err) {
          failed = true;
          throw err;
        }
      },
    });

    if (confirmed) {
      fetchItems();
      return;
    }

    if (failed) {
      await alert({
        title: t("common.deleteError"),
        description: t("error.generic"),
        variant: "danger",
      });
    }
  };

  return (
    <div className="w-full animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("sport.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("sport.subtitle")}
          </p>
        </div>
        <Button onClick={() => navigate("/app/sports/new")}>
          <Plus className="h-4 w-4 mr-2" />
          {t("sport.new")}
        </Button>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("sport.searchPlaceholder")}
            className="pl-9"
          />
        </div>
      </div>

      <Card className="w-full">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <Medal className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t("sport.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("sport.name")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("sport.categories")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("sport.events")}
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => navigate(`/app/sports/${item.id}/edit`)}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 font-medium">{item.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {item.category_count}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {item.event_count}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div
                          className="flex items-center justify-end gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Atalho para as categorias ja filtradas por esta
                              modalidade. */}
                          <button
                            onClick={() =>
                              navigate(`/app/sport-categories?sport=${item.id}`)
                            }
                            className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            title={t("sport.viewCategories")}
                          >
                            <Tags className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/app/sports/${item.id}/edit`)}
                            className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            title={t("common.edit")}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-muted-foreground hover:text-red-600"
                            title={t("common.delete")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
